'use client'

import { useState } from 'react'

import { formatPublishDate } from '@/lib/blog/dates'
import type { BlogPostMeta } from '@/lib/blog/schema'
import { cn } from '@/lib/utils'

/** Announcements shown per page; the rest are reachable via numbered pagination. */
const POSTS_PER_PAGE = 3

/**
 * A single announcement card — hero image beside the post's title, excerpt, and
 * date. Links to the bare `/blog/<slug>/` (the post's canonical URL); a plain
 * anchor, not the i18n `Link`, so it targets the un-prefixed canonical directly
 * instead of the `/en-us/...` form that 301s back to it.
 */
export function AnnouncementCard({ post }: { post: BlogPostMeta }) {
    const href = `/blog/${post.slug}/`

    return (
        <article className="grid overflow-hidden rounded-[3px] bg-gray-50 md:grid-cols-[minmax(0,280px)_1fr]">
            {post.heroImage ? (
                <a href={href} className="block">
                    {/* Final static-assets URL, baked in at migration time;
                        plain <img>, so no next/image loader config is needed. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src={post.heroImage.url}
                        alt={post.heroImage.alt}
                        width={post.heroImage.width}
                        height={post.heroImage.height}
                        className="h-full w-full object-cover"
                    />
                </a>
            ) : null}
            <div className="flex flex-col justify-center gap-2 bg-white p-5 md:p-6">
                <h3 className="text-xl font-bold text-[#222]">
                    <a href={href} className="hover:text-[#087484]">
                        {post.title}
                    </a>
                </h3>
                <span aria-hidden="true" className="h-0.5 w-16 bg-[#5bc2d5]" />
                <p className="leading-relaxed text-[#54595f]">{post.excerpt}</p>
                <time dateTime={post.publishedAt} className="text-sm text-[#89909a]">
                    {formatPublishDate(post.publishedAt)}
                </time>
            </div>
        </article>
    )
}

/**
 * The "Latest Announcements" feed — a fixed number of cards per page
 * ({@link POSTS_PER_PAGE}) with numbered pagination at the bottom. This keeps the
 * page short (so the Press Contact / Press Images blocks stay reachable) while
 * making it obvious there are more posts than the ones on screen. All posts are
 * static build data, so paging is pure client-side slicing — no fetching.
 */
export function AnnouncementsFeed({ posts }: { posts: BlogPostMeta[] }) {
    const [page, setPage] = useState(1)

    if (!posts.length) {
        return <p className="text-center text-[#54595f]">No announcements yet.</p>
    }

    const pageCount = Math.ceil(posts.length / POSTS_PER_PAGE)
    // Guard against a stale page if `posts` ever shrinks below the current page.
    const currentPage = Math.min(page, pageCount)
    const start = (currentPage - 1) * POSTS_PER_PAGE
    const visiblePosts = posts.slice(start, start + POSTS_PER_PAGE)

    return (
        <div>
            <div className="flex flex-col gap-6">
                {visiblePosts.map((post) => (
                    <AnnouncementCard key={post.slug} post={post} />
                ))}
            </div>

            {pageCount > 1 ? (
                <nav aria-label="Announcements pagination" className="mt-10">
                    <ul className="flex flex-wrap justify-center gap-2">
                        {Array.from({ length: pageCount }, (_, i) => i + 1).map((pageNumber) => {
                            const isCurrent = pageNumber === currentPage
                            return (
                                <li key={pageNumber}>
                                    <button
                                        type="button"
                                        onClick={() => setPage(pageNumber)}
                                        aria-current={isCurrent ? 'page' : undefined}
                                        className={cn(
                                            'flex h-10 w-10 items-center justify-center rounded-[3px] text-sm font-bold transition-colors',
                                            isCurrent
                                                ? 'bg-[#087484] text-white'
                                                : 'bg-gray-50 text-[#54595f] hover:bg-[#e6f3f5] hover:text-[#087484]',
                                        )}
                                    >
                                        {pageNumber}
                                    </button>
                                </li>
                            )
                        })}
                    </ul>
                </nav>
            ) : null}
        </div>
    )
}
